import { useState, useCallback } from "react";

export const COLUMN_STORAGE_KEYS = {
  // financial report columns
  SALES_REPORT_COLUMNS: "sales_report_columns",
  COMMISSION_REPORT_COLUMNS: "commission_report_columns",
  SALES_BY_SERVICE_REPORT_COLUMNS: "sales_by_service_report_columns",
  DUE_REPORT_COLUMNS: "due_report_columns",
  RECEIPT_REPORT_COLUMNS: "receipt_report_columns",
  CLIENT_LEDGER_REPORT_COLUMNS: "client_ledger_report_columns",
  SERVICE_PROVIDER_CHARGES_REPORT_COLUMNS: "service_provider_charges_report_columns",

  // patient report columns
  APPOINTMENT_REPORT_COLUMNS: "appointment_report_columns",
  PATIENT_ASSESSMENT_REPORT_COLUMNS: "patient_assessment_report_columns",
  CLIENT_REPORT_COLUMNS: "client_report_columns",

  // stock reports
  STOCK_AUDIT_REPORT_COLUMNS: "stock_audit_report_columns",
  STOCK_CASH_FLOW_REPORT_COLUMNS: "stock_cash_flow_report_columns",
  SUPPLIER_LEDGER_REPORT_COLUMNS: "supplier_ledger_report_columns",
  STOCK_TRANSACTION_REPORT_COLUMNS: "stock_transaction_report_columns",

  // message reports
  MESSAGE_REPORT_COLUMNS: "message_report_columns",

  // lab reports
  LAB_REPORT_COLUMNS: "lab_report_columns",

  // client sharing reports
  CLIENT_SHARING_REPORT_COLUMNS: "client_sharing_report_columns"
} as const;

export type ColumnStorageKey = (typeof COLUMN_STORAGE_KEYS)[keyof typeof COLUMN_STORAGE_KEYS];

export interface ColumnVisibility {
  [key: string]: boolean;
}

const getColumnsFromStorage = (
  storageKey: ColumnStorageKey,
  defaultColumns: ColumnVisibility
): ColumnVisibility => {
  try {
    const savedColumns = localStorage.getItem(storageKey);
    if (savedColumns) {
      const parsed = JSON.parse(savedColumns);
      if (typeof parsed === "object" && parsed !== null) {
        return parsed;
      }
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(
      `Failed to load column visibility from localStorage (key: ${storageKey}):`,
      error
    );
  }
  return defaultColumns;
};

const saveColumnsToStorage = (storageKey: ColumnStorageKey, columns: ColumnVisibility): void => {
  try {
    localStorage.setItem(storageKey, JSON.stringify(columns));
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error(`Failed to save column visibility to localStorage (key: ${storageKey}):`, error);
  }
};

export const useListColumnVisibility = (
  storageKey: ColumnStorageKey,
  defaultColumns: ColumnVisibility
): {
  columns: ColumnVisibility;
  setColumns: (updatedColumns: ColumnVisibility) => void;
} => {
  const [columns, setColumnsState] = useState<ColumnVisibility>(() =>
    getColumnsFromStorage(storageKey, defaultColumns)
  );

  const setColumns = useCallback(
    (updatedColumns: ColumnVisibility) => {
      setColumnsState(updatedColumns);
      saveColumnsToStorage(storageKey, updatedColumns);
    },
    [storageKey]
  );

  return { columns, setColumns };
};
